import React, { useState } from 'react';
import styles from './AppointmentForm.scss';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createAppointment } from 'actions/appointment.js';
import { ButtonSpinner } from '../../../common/ButtonSpinner/ButtonSpinner.jsx';
import Notification from 'components/Notification';
import { gaEvent, trackGA } from '../../../../services/ga.js';

const formatDateTime = (dateString, timeString) => {
  const [day, month, year] = dateString.split('.');

  const formattedDate = `${year}-${month}-${day}`;

  const date = new Date(formattedDate);

  const formattedDateString = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return `${formattedDateString}, ${timeString}`;
};

const AppointmentForm = ({
  selectedSlot,
  selectedDate,
  buildingId,
  type,
  handleSetResult,
  buildingName,
  objectType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formattedDateTime = formatDateTime(selectedDate, selectedSlot.from);
  const profile = useSelector((state) => state.profile);
  const { firstName, lastName, agencyName } = profile;
  const [isLoading, setIsLoading] = useState(false);

  const [formValues, setFormValues] = useState({
    buildingId,
    type,
    dateTimeFrom: formattedDateTime,
    name: `${firstName} ${lastName}`,
    companyName: agencyName,
    clientName: '',
    clientNumber: '',
    comment: '',
  });

  const [errors, setErrors] = useState({
    clientName: '',
    clientNumber: '',
  });

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formValues.clientName.trim()) {
      newErrors.clientName = t(
        'components.appointment.appointmentDialog.appointmentForm.clientNameRequired'
      );
      isValid = false;
    }

    if (!/^\d{4}$/.test(formValues.clientNumber)) {
      newErrors.clientNumber = t(
        'components.appointment.appointmentDialog.appointmentForm.clientNumberRequired'
      );
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    let digitOnlyValue = value;

    if (field === 'clientNumber') {
      digitOnlyValue = value.replace(/\D/g, '');
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: digitOnlyValue,
    }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (field === 'clientName' && digitOnlyValue.trim().length >= 2) {
        delete updatedErrors.clientName;
      }
      if (field === 'clientNumber' && /^\d{4}$/.test(digitOnlyValue)) {
        delete updatedErrors.clientNumber;
      }
      return updatedErrors;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      dispatch({
        type: 'SHOW_NOTIFICATION',
        payload: {
          message: '123',
          status: 1,
        },
      });
      return;
    }

    setIsLoading(true);

    const appointmentData = {
      buildingId,
      type,
      dateTimeFrom: `${selectedDate} ${selectedSlot.from}:00`,
      firstNameClient: formValues.clientName,
      phoneClient: formValues.clientNumber,
      comment: formValues.comment,
    };

    try {
      const result = await dispatch(createAppointment(appointmentData));

      if (result.data) {
        handleSetResult('success');
      } else {
        handleSetResult('error');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.AppointmentForm}>
      <form className={styles.InputGroup} onSubmit={handleSubmit}>
        <div className={styles.Title}>
          {t('components.appointment.appointmentDialog.appointmentForm.generalInfo')}
        </div>
        <div className={styles.InputWrap}>
          <input
            className={cn(styles.Input)}
            type="text"
            name="name"
            value={formValues.name}
            onChange={handleInputChange('name')}
            disabled
          />
        </div>
        {agencyName && (
          <div className={styles.InputWrap}>
            <input
              className={cn(styles.Input)}
              type="text"
              name="companyName"
              value={formValues.companyName}
              onChange={handleInputChange('companyName')}
              disabled
            />
          </div>
        )}
        <div className={styles.InputWrap}>
          <input
            className={cn(styles.Input, { [styles.Error]: !!errors.clientName })}
            type="text"
            name="clientName"
            value={formValues.clientName}
            onChange={handleInputChange('clientName')}
            placeholder={`${t(
              'components.appointment.appointmentDialog.appointmentForm.clientName'
            )}*`}
            maxLength={20}
          />
          {errors.clientName && <label className={styles.ErrorLabel}>{errors.clientName}</label>}
        </div>
        <div className={styles.InputWrap}>
          <input
            className={cn(styles.Input, { [styles.Error]: !!errors.clientNumber })}
            type="text"
            name="clientNumber"
            value={formValues.clientNumber}
            onChange={handleInputChange('clientNumber')}
            placeholder={`${t(
              'components.appointment.appointmentDialog.appointmentForm.clientNumber'
            )}*`}
            maxLength={4}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          {errors.clientNumber && (
            <label className={styles.ErrorLabel}>{errors.clientNumber}</label>
          )}
        </div>
        <div className={styles.InputWrap}>
          <input
            className={cn(styles.Input, { [styles.Touched]: !!formValues.comment })}
            type="text"
            name="comment"
            value={formValues.comment}
            onChange={handleInputChange('comment')}
            placeholder={t('components.appointment.appointmentDialog.appointmentForm.comment')}
            maxLength={250}
          />
        </div>
        <div className={styles.SelectedDate}>
          <div className={styles.SelectedDateTitle}>
            {t('components.appointment.appointmentDialog.appointmentForm.selectedDate')}
          </div>
          <div className={styles.SelectedDateText}>{formattedDateTime}</div>
        </div>
        <button
          onClick={() => {
            trackGA(
              gaEvent.appointmentDialogButtonsClick({
                eventLabel: 'zapisatsya',
                eventContent: buildingName,
                productType: objectType,
                productName: buildingName,
                productID: buildingId,
                user_id: profile.id,
              })
            );
          }}
          type="submit"
          className={cn(styles.SubmitButton, 'button-primary button-primary--md')}
          disabled={isLoading}
        >
          {isLoading ? (
            <ButtonSpinner />
          ) : (
            t('components.appointment.appointmentDialog.submitButton')
          )}
        </button>
      </form>
    </div>
  );
};

export default AppointmentForm;



// WEBPACK FOOTER //
// ./src/components/Appointment/AppointmentDialog/AppointmentForm/AppointmentForm.jsx